import { ReactNode } from 'react';

import { Badge } from '@components/badge';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useLocale, useScopedTranslate } from '@locale/translate';

import type { JobLabelItem } from 'types/jobs';

export const MAX_LABELS_ON_LIST = 5;

type JobLabelsProps = {
  labels: JobLabelItem[];
  /** Two or more labels over the cap collapse into "N more". */
  max?: number;
  leading?: ReactNode;
};

export const JobLabels = ({ labels, max, leading }: JobLabelsProps) => {
  const { t } = useScopedTranslate(locale);
  const lang = useLocale();

  if (labels.length === 0 && !leading) return null;

  const sorted = [...labels].sort((a, b) => a.title.localeCompare(b.title, lang));
  const visible = max != null && sorted.length > max + 1 ? sorted.slice(0, max) : sorted;
  const overflow = sorted.slice(visible.length);
  const overflowTitles = overflow.map((label) => label.title).join(', ');

  return (
    <Stack orientation="horizontal" space={1} wrap align="center">
      {leading}
      {visible.map((label) => (
        <Badge key={label.id} customColor={label.color} text={label.title} />
      ))}
      {overflow.length > 0 && (
        <Text
          font="regular12"
          color="textHelper"
          hoverColor="textSecondary"
          text={t('more', String(overflow.length))}
          tooltip={overflowTitles}
          // Keyboard and screen-reader access to the tooltip-only titles.
          tabIndex={0}
          role="img"
          aria-label={overflowTitles}
        />
      )}
    </Stack>
  );
};

const locale = {
  en: { more: '{0} more...' },
  is: { more: '{0} í viðbót...' },
};
